<script setup lang="ts">
/**
 * Single-line note field (Phase 5). Collapsed by default: shows the current
 * note text or a "+ Note" affordance. Expands to an inline input; saves on
 * Enter / Save / blur, cancels on Esc. Persistence is the parent's job (emits
 * `save`); this component is presentation only.
 */
import { ref, watch } from 'vue'

const props = withDefaults(defineProps<{ text: string; label: string; disabled?: boolean }>(), { disabled: false })
const emit = defineEmits<{ save: [text: string] }>()

const open = ref(false)
const draft = ref(props.text)
const inputEl = ref<HTMLInputElement | null>(null)

// Keep the draft in sync with the saved value while collapsed.
watch(() => props.text, value => { if (!open.value) draft.value = value })

function startEdit() {
  if (props.disabled) return
  draft.value = props.text
  open.value = true
}
function save() {
  if (!open.value) return
  open.value = false
  if (draft.value.trim() !== props.text) emit('save', draft.value)
}
function cancel() {
  draft.value = props.text
  open.value = false
}
</script>

<template>
  <div class="note-field">
    <button
      v-if="!open"
      type="button"
      class="note-field__toggle"
      :class="{ 'has-note': !!text }"
      :disabled="disabled"
      :aria-label="text ? `Edit note for ${label}` : `Add note for ${label}`"
      @click="startEdit"
    >
      <span v-if="text" class="note-field__text">{{ text }}</span>
      <span v-else class="note-field__add">+ Note</span>
    </button>

    <div v-else class="note-field__edit">
      <input
        ref="inputEl"
        v-model="draft"
        type="text"
        maxlength="140"
        :aria-label="`Note for ${label}`"
        placeholder="Add a quick note…"
        @keyup.enter="save"
        @keyup.esc="cancel"
        @blur="save"
      />
      <button type="button" class="note-field__save" @mousedown.prevent="save">Save</button>
    </div>
  </div>
</template>

<style scoped>
.note-field {
  margin-top: 4px;
}
.note-field__toggle {
  width: 100%;
  text-align: left;
  border: none;
  background: none;
  padding: 2px 0;
  cursor: pointer;
  color: var(--muted);
  line-height: 1.4;
}
.note-field__toggle.has-note {
  color: var(--text);
}
.note-field__toggle:disabled {
  cursor: default;
  opacity: 0.58;
}
.note-field__text {
  display: block;
  white-space: pre-wrap;
  word-break: break-word;
}
.note-field__text::before {
  content: '✎ ';
  color: var(--accent);
}
.note-field__add {
  color: var(--accent);
  opacity: 0.85;
}
.note-field__edit {
  display: flex;
  gap: 8px;
  align-items: center;
}
.note-field__edit input {
  flex: 1;
  min-width: 0;
  font-size: 16px; /* iOS: prevent focus zoom */
  padding: 6px 8px;
  border: 1px solid var(--border2);
  border-radius: 6px;
  background: var(--bg);
  color: var(--text);
}
.note-field__save {
  flex-shrink: 0;
  min-height: 32px;
  padding: 5px 10px;
  border: 1px solid var(--accent);
  border-radius: 6px;
  background: transparent;
  color: var(--accent);
  cursor: pointer;
}
</style>
